import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Rotate3d, ChevronLeft, ChevronRight, Maximize2, Sun, Moon } from "lucide-react";
import { ROOMS } from "../data";
import { useLang } from "../i18n";
import { Lightbox } from "../components/Lightbox";
import { Villa3D } from "../components/Villa3D";

export const FloorPlan = () => {
  const { t } = useLang();
  const [active, setActive] = useState("salon");
  const [slide, setSlide] = useState(0);
  const [lb, setLb] = useState(null);
  const [night, setNight] = useState(false);
  const room = ROOMS.find((r) => r.id === active);
  const label = (id) => t.plan.rooms[id] || id;
  const n = room.photos.length;

  const selectRoom = (id) => { setActive(id); setSlide(0); };
  const next = () => setSlide((s) => (s + 1) % n);
  const prev = () => setSlide((s) => (s - 1 + n) % n);

  return (
    <section id="plan" className="bg-sand-deep border-y border-[var(--line)] py-24 md:py-36" data-testid="floorplan-section">
      <div className="px-6 md:px-12 max-w-[1600px] mx-auto">
        <div className="max-w-2xl mb-14 md:mb-20">
          <span className="text-[11px] tracking-[0.35em] uppercase text-terracotta">{t.plan.eyebrow}</span>
          <h2 className="font-serif font-light text-4xl md:text-6xl mt-4 tracking-tight">{t.plan.title}</h2>
          <p className="text-ink-soft font-light mt-5 flex items-center gap-2">
            <Rotate3d className="w-5 h-5 text-terracotta shrink-0" strokeWidth={1.5} />
            {t.plan.hint}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 items-start">
          {/* 3D model */}
          <div className="lg:col-span-7 min-w-0 w-full">
            <div
              className="relative bg-sand-bg border border-[var(--line)] h-[380px] md:h-[520px] overflow-hidden"
              style={{ boxShadow: "0 30px 60px -30px rgba(45,48,42,0.35)" }}
            >
              <Villa3D active={active} onSelect={selectRoom} labels={t.plan.rooms} night={night} />
              <button
                onClick={() => setNight((v) => !v)}
                data-testid="toggle-night"
                aria-label={night ? "Mode jour" : "Mode nuit"}
                className={`absolute top-3 right-3 z-10 flex items-center gap-2 px-3 h-10 text-xs tracking-wide transition-colors duration-300 ${
                  night ? "bg-sand-bg text-ink hover:bg-terracotta hover:text-white" : "bg-ink/75 text-white hover:bg-terracotta"
                }`}
              >
                {night ? <Sun className="w-4 h-4" strokeWidth={1.5} /> : <Moon className="w-4 h-4" strokeWidth={1.5} />}
                {night ? t.plan.dayLabel : t.plan.nightLabel}
              </button>
              <span className={`absolute bottom-3 left-4 text-[10px] tracking-[0.2em] uppercase pointer-events-none ${night ? "text-white/50" : "text-ink-soft/70"}`}>
                {t.plan.dragHint}
              </span>
            </div>

            <div className="flex flex-wrap gap-2 mt-6">
              {ROOMS.map((r) => (
                <button
                  key={r.id}
                  onClick={() => selectRoom(r.id)}
                  data-testid={`room-chip-${r.id}`}
                  className={`text-sm px-5 py-2.5 border transition-colors duration-300 ${
                    r.id === active ? "bg-ink text-sand-bg border-ink" : "border-[var(--line)] text-ink-soft hover:border-ink hover:text-ink"
                  }`}
                >
                  {label(r.id)}
                </button>
              ))}
            </div>
          </div>

          {/* Slideshow gallery */}
          <div className="lg:col-span-5 min-w-0 w-full" data-testid="room-slideshow">
            <div className="flex items-baseline justify-between mb-5">
              <h3 className="font-serif text-3xl md:text-4xl font-light">{label(active)}</h3>
              <span className="text-ink-soft text-sm tabular-nums" data-testid="slide-counter">{slide + 1} / {n}</span>
            </div>

            <div className="relative group overflow-hidden aspect-[4/3] bg-sand-bg border border-[var(--line)]">
              <AnimatePresence mode="wait">
                <motion.img
                  key={`${active}-${slide}`}
                  src={room.photos[slide]}
                  alt={`${label(active)} ${slide + 1}`}
                  initial={{ opacity: 0, scale: 1.04 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5, ease: [0.19, 1, 0.22, 1] }}
                  drag={n > 1 ? "x" : false}
                  dragConstraints={{ left: 0, right: 0 }}
                  dragElastic={0.15}
                  onDragEnd={(e, info) => {
                    if (info.offset.x < -60) next();
                    else if (info.offset.x > 60) prev();
                  }}
                  className="absolute inset-0 h-full w-full object-cover cursor-zoom-in touch-pan-y"
                  onClick={() => setLb(slide)}
                  data-testid="slideshow-image"
                />
              </AnimatePresence>
              <button
                onClick={() => setLb(slide)}
                data-testid="slideshow-fullscreen"
                aria-label="Plein écran"
                className="absolute top-3 right-3 w-9 h-9 bg-ink/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Maximize2 className="w-4 h-4" strokeWidth={1.5} />
              </button>
              {n > 1 && (
                <>
                  <button
                    onClick={prev}
                    data-testid="slideshow-prev"
                    aria-label="Photo précédente"
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-sand-bg/85 backdrop-blur flex items-center justify-center hover:bg-terracotta hover:text-white transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                  <button
                    onClick={next}
                    data-testid="slideshow-next"
                    aria-label="Photo suivante"
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-sand-bg/85 backdrop-blur flex items-center justify-center hover:bg-terracotta hover:text-white transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </>
              )}
            </div>

            {n > 1 && (
              <div className="flex gap-2 mt-4 overflow-x-auto pb-2" data-testid="slideshow-thumbs">
                {room.photos.map((src, i) => (
                  <button
                    key={i}
                    onClick={() => setSlide(i)}
                    data-testid={`slideshow-thumb-${i}`}
                    className={`shrink-0 w-16 h-12 overflow-hidden border transition-all duration-300 ${
                      i === slide ? "border-terracotta opacity-100" : "border-transparent opacity-50 hover:opacity-80"
                    }`}
                  >
                    <img src={src} alt="" className="h-full w-full object-cover" loading="lazy" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <Lightbox images={room.photos} index={lb} onClose={() => setLb(null)} onNav={setLb} />
    </section>
  );
};
